import { apiClient, buildApiUrl } from '../lib/apiClient';
import type { GalleryImage } from '../types/gallery';

/** An image created by the gallery upload endpoint (only the fields callers need). */
export interface UploadedGalleryImage {
  id: string;
  /**
   * Storage object key (`aquariums/<aquariumId>/images/…`) — what AI features such as
   * the ZeoVit recommendation take as a photo reference. Absent if the API omits it.
   */
  s3Key?: string;
}

function parseUploadedImages(responseText: string): UploadedGalleryImage[] {
  try {
    const body = JSON.parse(responseText) as { data?: Array<{ id?: unknown; s3_key?: unknown }> };
    if (!Array.isArray(body.data)) return [];
    return body.data
      .filter((img): img is { id: string; s3_key?: unknown } => typeof img?.id === 'string')
      .map((img) => (typeof img.s3_key === 'string' ? { id: img.id, s3Key: img.s3_key } : { id: img.id }));
  } catch {
    return [];
  }
}

/**
 * Uploads images to the aquarium gallery (POST /aquariums/:aquariumId/images)
 * and resolves with the created images, so a caller can link them elsewhere
 * (e.g. to a journal entry) in a second step.
 */
export async function uploadGalleryImages(
  aquariumId: string,
  files: File[],
  onProgress?: (percent: number) => void,
): Promise<UploadedGalleryImage[]> {
  return new Promise<UploadedGalleryImage[]>((resolve, reject) => {
    const formData = new FormData();
    files.forEach((f) => formData.append('images', f));

    const xhr = new XMLHttpRequest();

    if (onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      });
    }

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(parseUploadedImages(xhr.responseText));
      } else {
        let message = `Upload failed (${xhr.status})`;
        try {
          const body = JSON.parse(xhr.responseText) as { error?: { message?: string } };
          if (body.error?.message) message = body.error.message;
        } catch { /* ignore */ }
        reject(new Error(message));
      }
    });

    xhr.addEventListener('error', () => reject(new Error('Network error during upload')));
    xhr.addEventListener('abort', () => reject(new Error('Upload aborted')));
    xhr.addEventListener('timeout', () => reject(new Error('Upload timed out')));
    xhr.timeout = 60_000;

    xhr.open('POST', buildApiUrl(`/api/v1/aquariums/${aquariumId}/images`));
    try {
      const raw = localStorage.getItem('reefguide_tokens');
      if (raw) {
        const parsed = JSON.parse(raw) as Record<string, unknown>;
        if (typeof parsed.accessToken === 'string') {
          xhr.setRequestHeader('Authorization', `Bearer ${parsed.accessToken}`);
        }
      }
    } catch { /* malformed JSON — proceed unauthenticated */ }
    xhr.send(formData);
  });
}

export interface GalleryImagesResponse {
  images: GalleryImage[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export interface FetchGalleryImagesParams {
  aquariumId: string;
  page?: number;
  limit?: number;
  /** Only images in this album. */
  albumId?: string | null;
  /** Only images carrying this tag. */
  tagId?: string | null;
}

type RawImage = {
  id: string;
  aquariumId: string;
  s3Key: string;
  thumbnailUrl: string | null;
  displayUrl: string | null;
  originalUrl: string | null;
  originalFilename: string;
  mimeType: string;
  fileSizeBytes: number;
  width: number | null;
  height: number | null;
  uploadedAt: string;
  /** Photo dates (follow-up of #1437); absent from older API responses. */
  takenAt?: string | null;
  capturedAt?: string | null;
  photoTakenAt?: string;
  hasAiAnalysis: boolean;
  tags: Array<{ id: string; name: string }>;
  albumIds: string[];
  noteContent: string | null;
};

export async function fetchGalleryImages(
  params: FetchGalleryImagesParams,
): Promise<GalleryImagesResponse> {
  const { aquariumId, page = 1, limit = 20, albumId, tagId } = params;

  const searchParams = new URLSearchParams({
    page: String(page),
    page_size: String(limit),
  });
  if (albumId) searchParams.set('album_id', albumId);
  if (tagId) searchParams.set('tags', tagId);

  const response = await apiClient.get<{
    data: {
      images: RawImage[];
      total: number;
      page: number;
      page_size: number;
      total_pages: number;
    };
  }>(`/api/v1/aquariums/${aquariumId}/images?${searchParams.toString()}`);

  const { data } = response;

  const images: GalleryImage[] = data.images.map((img: RawImage) => ({
    id: img.id,
    aquariumId: img.aquariumId,
    imageKey: img.s3Key,
    thumbnailKey: null,
    displayKey: null,
    displayUrl: img.displayUrl ?? img.originalUrl ?? null,
    thumbnailUrl: img.thumbnailUrl ?? img.displayUrl ?? img.originalUrl ?? null,
    originalFilename: img.originalFilename,
    mimeType: img.mimeType,
    fileSizeBytes: img.fileSizeBytes,
    width: img.width,
    height: img.height,
    uploadedAt: img.uploadedAt,
    photoTakenAt: img.photoTakenAt ?? img.capturedAt ?? img.takenAt ?? img.uploadedAt,
    takenAt: img.takenAt ?? null,
    capturedAt: img.capturedAt ?? null,
    hasAiAnalysis: img.hasAiAnalysis,
    tags: (img.tags ?? []).map((t) => t.name),
    tagRefs: (img.tags ?? []).map((t) => ({ id: t.id, name: t.name })),
    albumIds: img.albumIds ?? [],
    notes: img.noteContent,
  }));

  return {
    images,
    total: data.total,
    page: data.page,
    limit: data.page_size,
    hasMore: data.page * data.page_size < data.total,
  };
}

// ─── Gallery organisation: albums, tags, notes, journal references (#12) ─────

export interface GalleryAlbum {
  id: string;
  name: string;
}

export interface GalleryTag {
  id: string;
  name: string;
}

export interface ImageJournalReference {
  id: string;
  title: string | null;
  createdAt: string;
}

export async function listAlbums(aquariumId: string): Promise<GalleryAlbum[]> {
  const res = await apiClient.get<{ data: { albums: GalleryAlbum[] } }>(`/api/v1/aquariums/${aquariumId}/albums`);
  return res.data.albums.map((a) => ({ id: a.id, name: a.name }));
}

export async function createAlbum(aquariumId: string, name: string): Promise<GalleryAlbum> {
  const res = await apiClient.post<{ data: { album: GalleryAlbum } }>(`/api/v1/aquariums/${aquariumId}/albums`, { name });
  return { id: res.data.album.id, name: res.data.album.name };
}

export async function renameAlbum(aquariumId: string, albumId: string, name: string): Promise<GalleryAlbum> {
  const res = await apiClient.patch<{ data: { album: GalleryAlbum } }>(
    `/api/v1/aquariums/${aquariumId}/albums/${albumId}`,
    { name },
  );
  return { id: res.data.album.id, name: res.data.album.name };
}

export async function deleteAlbum(aquariumId: string, albumId: string): Promise<void> {
  await apiClient.delete(`/api/v1/aquariums/${aquariumId}/albums/${albumId}`);
}

export async function addImageToAlbum(aquariumId: string, albumId: string, imageId: string): Promise<void> {
  await apiClient.post(`/api/v1/aquariums/${aquariumId}/albums/${albumId}/images`, { imageId });
}

export async function removeImageFromAlbum(aquariumId: string, albumId: string, imageId: string): Promise<void> {
  await apiClient.delete(`/api/v1/aquariums/${aquariumId}/albums/${albumId}/images/${imageId}`);
}

export async function listTags(aquariumId: string): Promise<GalleryTag[]> {
  const res = await apiClient.get<{ data: { tags: GalleryTag[] } }>(`/api/v1/aquariums/${aquariumId}/tags`);
  return res.data.tags.map((t) => ({ id: t.id, name: t.name }));
}

/** Creates the tag (or returns the existing one with the same name). */
export async function createTag(aquariumId: string, name: string): Promise<GalleryTag> {
  const res = await apiClient.post<{ data: { tag: GalleryTag } }>(`/api/v1/aquariums/${aquariumId}/tags`, { name });
  return { id: res.data.tag.id, name: res.data.tag.name };
}

export async function addTagToImage(imageId: string, tagId: string): Promise<void> {
  await apiClient.post(`/api/v1/images/${imageId}/tags`, { tagId });
}

export async function removeTagFromImage(imageId: string, tagId: string): Promise<void> {
  await apiClient.delete(`/api/v1/images/${imageId}/tags/${tagId}`);
}

/** Saves the image note; an empty text deletes it. */
export async function saveImageNote(imageId: string, content: string): Promise<void> {
  if (content.trim() === '') {
    try {
      await apiClient.delete(`/api/v1/images/${imageId}/notes`);
    } catch (err) {
      // No note to delete is fine.
      if ((err as { status?: number }).status !== 404) throw err;
    }
    return;
  }
  await apiClient.put(`/api/v1/images/${imageId}/notes`, { content });
}

/**
 * Sets the date the photo was taken (`YYYY-MM-DD`), or clears it with null so the
 * photo date falls back to the upload date. Camera images refuse this (409).
 */
export async function saveImageTakenAt(imageId: string, date: string | null): Promise<void> {
  await apiClient.put(`/api/v1/images/${imageId}/taken-at`, { takenAt: date });
}

export async function listImageJournalReferences(
  aquariumId: string,
  imageId: string,
): Promise<ImageJournalReference[]> {
  const res = await apiClient.get<{ data: Array<{ id: string; title: string | null; createdAt: string }> }>(
    `/api/v1/aquariums/${aquariumId}/images/${imageId}/journal-references`,
  );
  return res.data.map((r) => ({ id: r.id, title: r.title, createdAt: r.createdAt }));
}

// ─── Deleting images ─────────────────────────────────────────────────────────

/** Deletes one gallery image (and its links to journal, animals, albums, …). */
export async function deleteGalleryImage(aquariumId: string, imageId: string): Promise<void> {
  await apiClient.delete(`/api/v1/aquariums/${aquariumId}/images/${imageId}`);
}

/** Max images per bulk request (backend MAX_BULK_DELETE). */
export const MAX_BULK_DELETE = 100;

/** Deletes several gallery images; all of them or none. Larger selections go in chunks. */
export async function deleteGalleryImages(aquariumId: string, imageIds: readonly string[]): Promise<string[]> {
  const deleted: string[] = [];
  for (let i = 0; i < imageIds.length; i += MAX_BULK_DELETE) {
    const chunk = imageIds.slice(i, i + MAX_BULK_DELETE);
    const res = await apiClient.post<{ data: { deletedIds: string[] } }>(
      `/api/v1/aquariums/${aquariumId}/images/bulk-delete`,
      { imageIds: chunk },
    );
    deleted.push(...res.data.deletedIds);
  }
  return deleted;
}

// ─── Per-image AI insights (#8) ──────────────────────────────────────────────

export type ImageInsightType =
  | 'species_identification'
  | 'algae_detection'
  | 'disease_detection'
  | 'growth_analysis';

export interface ImageInsight {
  id: string;
  analysisType: ImageInsightType;
  summary: string;
  observations: string[];
  confidence: number | null;
}

interface RawInsight {
  id: string;
  analysis_type: ImageInsightType;
  results: { summary?: unknown; observations?: unknown } | null;
  confidence_score: number | null;
}

function toInsight(raw: RawInsight): ImageInsight {
  const summary = typeof raw.results?.summary === 'string' ? raw.results.summary : '';
  const observations = Array.isArray(raw.results?.observations)
    ? raw.results.observations.filter((o): o is string => typeof o === 'string')
    : [];
  return { id: raw.id, analysisType: raw.analysis_type, summary, observations, confidence: raw.confidence_score };
}

export async function fetchImageInsights(imageId: string): Promise<ImageInsight[]> {
  const res = await apiClient.get<{ data: { insights: RawInsight[] } }>(`/api/v1/images/${imageId}/insights`);
  return res.data.insights.map(toInsight);
}

/** Runs the on-demand AI analysis of one image (uses AI credits) in the given UI language. */
export async function analyzeImage(imageId: string, language: string): Promise<ImageInsight[]> {
  const res = await apiClient.post<{ data: { insights: RawInsight[] } }>(`/api/v1/images/${imageId}/analyze`, {
    language,
  });
  return res.data.insights.map(toInsight);
}

export async function dismissImageInsight(imageId: string, insightId: string): Promise<void> {
  await apiClient.put(`/api/v1/images/${imageId}/insights/${insightId}/dismiss`);
}
